import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { ASSIGNABLE_ROLES } from '../../employeesApi';
import type { EmployeeRole } from '../../employeesApi';
import { useInviteEmployee } from '../../hooks';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface InviteEmployeeDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Invite-only onboarding: the platform never renders a password field. The invitee receives Keycloak's
 * own themed email and sets their own credentials there.
 */
export function InviteEmployeeDialog({ open, onClose }: InviteEmployeeDialogProps) {
  const { t } = useTranslation('employees');
  const invite = useInviteEmployee();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<EmployeeRole>('STAFF');
  const [emailUndelivered, setEmailUndelivered] = useState(false);

  const valid = EMAIL_RE.test(email.trim());

  const close = () => {
    invite.reset();
    setEmail('');
    setRole('STAFF');
    setEmailUndelivered(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>{t('inviteDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {invite.isError && <Alert severity="error">{invite.error?.message}</Alert>}
          {emailUndelivered && (
            <Alert severity="warning">{t('inviteDialog.emailUndelivered')}</Alert>
          )}
          <TextField
            label={t('inviteDialog.emailLabel')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={email.length > 0 && !valid}
            helperText={t('inviteDialog.emailHelper')}
            fullWidth
            autoFocus
          />
          <TextField
            select
            label={t('inviteDialog.roleLabel')}
            value={role}
            onChange={(e) => setRole(e.target.value as EmployeeRole)}
            helperText={t('inviteDialog.roleHelper')}
            fullWidth
          >
            {ASSIGNABLE_ROLES.map((r) => (
              <MenuItem key={r} value={r}>
                {t(`roles.${r.toLowerCase()}`)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={invite.isPending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!valid || invite.isPending}
          startIcon={invite.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() =>
            invite.mutate(
              { email: email.trim(), role },
              {
                onSuccess: (employee) => {
                  // Created and granted access, but the invitation mail did not go out: keep the dialog
                  // open and say so, because closing it cleanly would imply the person had been notified.
                  if (employee?.invitationEmailSent === false) {
                    setEmailUndelivered(true);
                    return;
                  }
                  close();
                },
              },
            )
          }
        >
          {t('inviteDialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
